import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

const ComparisonTableTemplate = ({ params }) => {
  const columns = params?.columns || [];
  const rows = params?.rows || [];
  const [activeCard, setActiveCard] = useState(0);
  const scrollRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const cardWidth = el.firstChild?.offsetWidth || 1;
      const gap = 12;
      const idx = Math.round(el.scrollLeft / (cardWidth + gap));
      setActiveCard(idx);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  if (columns.length === 0) {
    return <p style={{ color: 'var(--panel-text-secondary)' }}>No comparison data available.</p>;
  }

  // Mobile: swipeable cards (one card per column item)
  if (isMobile) {
    return (
      <div>
        <div className="comparison-cards" ref={scrollRef}>
          {columns.map((col, colIdx) => (
            <motion.div
              className="comparison-card"
              key={colIdx}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: colIdx * 0.1, duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            >
              <div className="comparison-card__title">{col}</div>
              {rows.map((row, rowIdx) => (
                <div className="comparison-card__row" key={rowIdx}>
                  <span className="comparison-card__attribute">{row.attribute}</span>
                  <span className="comparison-card__value">{row.values[colIdx] || '—'}</span>
                </div>
              ))}
            </motion.div>
          ))}
        </div>
        {columns.length > 1 && (
          <div className="comparison-cards__indicator">
            {columns.map((_, i) => (
              <div
                key={i}
                className={`comparison-cards__dot${i === activeCard ? ' comparison-cards__dot--active' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Desktop: table layout
  return (
    <motion.div
      className="comparison-table"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
    >
      <table>
        <thead>
          <tr>
            <th />
            {columns.map((col, i) => (
              <th key={i}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <motion.tr
              key={rowIdx}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: rowIdx * 0.05, duration: 0.25 }}
            >
              <td>{row.attribute}</td>
              {row.values.map((val, colIdx) => (
                <td key={colIdx}>{val || '—'}</td>
              ))}
            </motion.tr>
          ))}
        </tbody>
      </table>
    </motion.div>
  );
};

export default ComparisonTableTemplate;
